import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@lnr-v2/ui';
import { TagPill } from '../../atoms/TagPill';
import { useTerms } from '../../../hooks/useTerms';
import type { TermWithTaxonomy } from '../../../services/taxonomies.service';
import styles from './TermSelector.module.css';

export interface TermSelectorProps {
  selectedTermIds: string[]; // Array of term IDs
  onChange: (termIds: string[]) => void;
  taxonomySlug?: string; // Filter by taxonomy (e.g., 'services', 'industries')
  lang?: 'en' | 'fr';
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  groupByTaxonomy?: boolean; // Group terms by taxonomy in dropdown
}

export const TermSelector: React.FC<TermSelectorProps> = ({
  selectedTermIds,
  onChange,
  taxonomySlug,
  lang = 'en',
  placeholder = 'Search and select terms...',
  disabled = false,
  className,
  groupByTaxonomy = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [knownTerms, setKnownTerms] = useState<
    Record<string, TermWithTaxonomy>
  >({});

  const { terms, loading } = useTerms({
    search: searchQuery,
    taxonomySlug,
    isActive: true,
  });

  // Update known terms cache whenever terms are fetched
  useEffect(() => {
    if (terms.length > 0) {
      setKnownTerms((prev) => {
        const next = { ...prev };
        let hasNew = false;
        terms.forEach((term) => {
          if (!next[term.id]) {
            next[term.id] = term;
            hasNew = true;
          }
        });
        return hasNew ? next : prev;
      });
    }
  }, [terms]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get selected term objects (check current terms first, then cache)
  const selectedTermObjects = selectedTermIds
    .map((id) => terms.find((t) => t.id === id) || knownTerms[id])
    .filter((term): term is TermWithTaxonomy => !!term);

  // Get available terms (not selected)
  const availableTerms = terms.filter(
    (term) => !selectedTermIds.includes(term.id)
  );

  // Group terms by taxonomy if needed
  const groupedTerms = groupByTaxonomy
    ? availableTerms.reduce(
        (acc, term) => {
          const taxonomyName = term.taxonomy.name;
          if (!acc[taxonomyName]) {
            acc[taxonomyName] = [];
          }
          acc[taxonomyName].push(term);
          return acc;
        },
        {} as Record<string, TermWithTaxonomy[]>
      )
    : { '': availableTerms };

  const handleToggleTerm = (termId: string) => {
    if (selectedTermIds.includes(termId)) {
      onChange(selectedTermIds.filter((id) => id !== termId));
    } else {
      onChange([...selectedTermIds, termId]);
    }
  };

  const handleRemoveTerm = (termId: string) => {
    onChange(selectedTermIds.filter((id) => id !== termId));
  };

  const getTermName = (term: TermWithTaxonomy) => {
    return lang === 'fr' ? term.nameFR : term.nameEN;
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${disabled ? styles.disabled : ''} ${className || ''}`}
    >
      {/* Selected terms */}
      <div
        className={styles.selectedTerms}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {selectedTermObjects.length > 0 ? (
          selectedTermObjects.map((term) => (
            <TagPill
              key={term.id}
              label={getTermName(term)}
              color={term.color}
              icon={term.icon}
              size="sm"
              removable
              onRemove={() => handleRemoveTerm(term.id)}
            />
          ))
        ) : (
          <span className={styles.placeholder}>{placeholder}</span>
        )}

        {/* Search input */}
        <input
          ref={inputRef}
          type="text"
          className={styles.input}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (
              e.key === 'Backspace' &&
              searchQuery === '' &&
              selectedTermIds.length > 0
            ) {
              const newIds = [...selectedTermIds];
              newIds.pop();
              onChange(newIds);
            }
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={selectedTermObjects.length > 0 ? '' : placeholder}
          disabled={disabled}
        />

        {/* Dropdown toggle icon */}
        <button
          type="button"
          className={styles.toggleButton}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
        >
          <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size="sm" />
        </button>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className={styles.dropdown}>
          {loading ? (
            <div className={styles.loading}>
              <div className={styles.spinner} />
              <span>Loading terms...</span>
            </div>
          ) : Object.keys(groupedTerms).length > 0 ? (
            <div className={styles.list}>
              {Object.entries(groupedTerms).map(
                ([taxonomyName, groupTerms]) => (
                  <div key={taxonomyName || 'default'} className={styles.group}>
                    {groupByTaxonomy && taxonomyName && (
                      <div className={styles.groupHeader}>{taxonomyName}</div>
                    )}
                    <ul className={styles.groupList}>
                      {groupTerms.map((term) => (
                        <li
                          key={term.id}
                          className={styles.listItem}
                          onClick={() => handleToggleTerm(term.id)}
                        >
                          <div className={styles.termInfo}>
                            {term.icon && <Icon name={term.icon} size="sm" />}
                            <span className={styles.termName}>
                              {getTermName(term)}
                            </span>
                            {!groupByTaxonomy && term.taxonomy && (
                              <span className={styles.taxonomyBadge}>
                                {term.taxonomy.name}
                              </span>
                            )}
                          </div>
                          {term.usageCount !== undefined &&
                            term.usageCount > 0 && (
                              <span className={styles.usageCount}>
                                {term.usageCount}{' '}
                                {term.usageCount === 1 ? 'use' : 'uses'}
                              </span>
                            )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              )}
            </div>
          ) : (
            <div className={styles.empty}>
              {searchQuery
                ? `No terms found for "${searchQuery}"`
                : 'No terms available'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
